import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  Platform,
  Linking,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const API_BASE = 'https://collabbay.com/APIControllers';
const ASSET_BASE = 'https://collabbayadmin.com';
const VOUCHER_PAGE_BASE = 'https://collabbay.com/Voucher';

const CreatorVouchers = ({ route }) => {
  const routePromoter =
    route?.params?.Promoter ||
    route?.params?.promoter ||
    route?.params?.Username ||
    route?.params?.username ||
    '';

  const Promoter = useMemo(() => {
    if (routePromoter) {
      return String(routePromoter).trim();
    }

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const pathname = window.location.pathname || '';
      const pathParts = pathname.split('/').filter(Boolean);
      const vouchersRouteIndex = pathParts.findIndex(
        (item) => item.toLowerCase() === 'vouchers'
      );

      if (
        vouchersRouteIndex !== -1 &&
        pathParts[vouchersRouteIndex + 1]
      ) {
        try {
          return decodeURIComponent(pathParts[vouchersRouteIndex + 1]).trim();
        } catch (error) {
          return String(pathParts[vouchersRouteIndex + 1]).trim();
        }
      }
    }

    return '';
  }, [routePromoter]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [creator, setCreator] = useState(null);
  const [offers, setOffers] = useState([]);
  const [summary, setSummary] = useState({
    TotalOffers: 0,
    LiveOffers: 0,
    AvailableStock: 0,
  });

  useEffect(() => {
    fetchCreatorProfile();
  }, [Promoter]);

  const normalizeAssetUrl = (value) => {
    if (!value) {
      return null;
    }

    const rawValue = String(value).trim();

    if (!rawValue) {
      return null;
    }

    if (
      rawValue.startsWith('https://') ||
      rawValue.startsWith('http://')
    ) {
      return rawValue;
    }

    if (rawValue.startsWith('//')) {
      return `https:${rawValue}`;
    }

    if (rawValue.startsWith('/')) {
      return `${ASSET_BASE}${rawValue}`;
    }

    return `${ASSET_BASE}/${rawValue}`;
  };

  const getInitials = (value) => {
    const cleanValue = String(value || '').trim();

    if (!cleanValue) {
      return '?';
    }

    const parts = cleanValue.split(' ').filter(Boolean);

    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  };

  const formatNumber = (value) => {
    const parsedNumber = Number(value || 0);

    if (Number.isNaN(parsedNumber)) {
      return '0';
    }

    return new Intl.NumberFormat('en-GB').format(parsedNumber);
  };

  const getMediaTypeFromUrl = (url) => {
    const cleanUrl = String(url || '').split('?')[0].toLowerCase();

    if (
      cleanUrl.endsWith('.mp4') ||
      cleanUrl.endsWith('.mov') ||
      cleanUrl.endsWith('.webm') ||
      cleanUrl.endsWith('.m4v')
    ) {
      return 'video';
    }

    if (
      cleanUrl.endsWith('.jpg') ||
      cleanUrl.endsWith('.jpeg') ||
      cleanUrl.endsWith('.png') ||
      cleanUrl.endsWith('.gif') ||
      cleanUrl.endsWith('.webp') ||
      cleanUrl.endsWith('.avif')
    ) {
      return 'image';
    }

    return 'image';
  };

  const getOfferMedia = (offer) => {
    if (offer?.contentMedia?.url) {
      const finalUrl = normalizeAssetUrl(offer.contentMedia.url);

      return {
        url: finalUrl,
        type: offer.contentMedia.type || getMediaTypeFromUrl(finalUrl),
      };
    }

    if (offer?.postUpload?.Image) {
      const finalUrl = normalizeAssetUrl(offer.postUpload.Image);

      return {
        url: finalUrl,
        type: getMediaTypeFromUrl(finalUrl),
      };
    }

    return null;
  };

  const requestJson = async (url) => {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    const rawResponse = await response.text();

    let data;

    try {
      data = JSON.parse(rawResponse);
    } catch (error) {
      throw new Error('The server returned invalid JSON.');
    }

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'The request could not be completed.');
    }

    return data;
  };

  const enrichOfferWithExistingVoucherContent = async (offer) => {
    try {
      const detailUrl = `${API_BASE}/FetchVoucherInformation.php?CollabID=${encodeURIComponent(
        offer.CollabID
      )}`;

      const detailData = await requestJson(detailUrl);

      return {
        ...(detailData.voucher || {}),
        ...offer,
        postUpload: detailData.postUpload || null,
        contentMedia: detailData.contentMedia || null,
      };
    } catch (error) {
      /*
       * The public profile must still render the offer even where an old
       * collab has no uploaded media or the detail endpoint fails for it.
       */
      return {
        ...offer,
        postUpload: null,
        contentMedia: null,
      };
    }
  };

  const fetchCreatorProfile = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setLoadError('');

      if (!Promoter) {
        setCreator(null);
        setOffers([]);
        setLoadError('No creator username was included in this link.');
        return;
      }

      const profileUrl = `${API_BASE}/GetCreatorVoucherProfile.php?Promoter=${encodeURIComponent(
        Promoter
      )}`;

      const profileData = await requestJson(profileUrl);

      const rawOffers = Array.isArray(profileData.offers)
        ? profileData.offers
        : [];

      const completedOffers = await Promise.all(
        rawOffers.map((offer) =>
          enrichOfferWithExistingVoucherContent(offer)
        )
      );

      const firstOfferWithCreatorImage = completedOffers.find(
        (offer) => offer.PromoterProfileImage
      );

      setCreator({
        Promoter: profileData.Promoter || Promoter,
        ProfileImage:
          firstOfferWithCreatorImage?.PromoterProfileImage || null,
      });

      setSummary({
        TotalOffers:
          Number(profileData?.summary?.TotalOffers) ||
          completedOffers.length,
        LiveOffers:
          Number(profileData?.summary?.LiveOffers) ||
          completedOffers.filter((offer) => Number(offer.Stock || 0) > 0)
            .length,
        AvailableStock:
          Number(profileData?.summary?.AvailableStock) ||
          completedOffers.reduce(
            (total, offer) =>
              total + Math.max(Number(offer.Stock || 0), 0),
            0
          ),
      });

      setOffers(completedOffers);
    } catch (error) {
      setCreator({
        Promoter,
        ProfileImage: null,
      });

      setOffers([]);
      setSummary({
        TotalOffers: 0,
        LiveOffers: 0,
        AvailableStock: 0,
      });

      setLoadError(
        error?.message ||
          'Something went wrong loading this creator profile.'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const openVoucherLink = async (CollabID) => {
    if (!CollabID) {
      return;
    }

    const voucherUrl = `${VOUCHER_PAGE_BASE}/${encodeURIComponent(
      CollabID
    )}`;

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.assign(voucherUrl);
      return;
    }

    try {
      await Linking.openURL(voucherUrl);
    } catch (error) {
      console.error('Unable to open voucher:', error);
    }
  };

  const renderCreatorAvatar = () => {
    const profileImage = normalizeAssetUrl(creator?.ProfileImage);

    return (
      <View style={styles.creatorAvatarOuter}>
        {profileImage ? (
          <Image
            source={{ uri: profileImage }}
            resizeMode="cover"
            style={styles.creatorAvatarImage}
          />
        ) : (
          <View style={styles.creatorAvatarFallback}>
            <Text style={styles.creatorAvatarFallbackText}>
              {getInitials(creator?.Promoter || Promoter)}
            </Text>
          </View>
        )}
      </View>
    );
  };

  const renderGradientFade = () => {
    if (Platform.OS === 'web') {
      return React.createElement('div', {
        style: {
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          pointerEvents: 'none',
          background:
            'linear-gradient(to top, rgba(0,0,0,0.93) 0%, rgba(0,0,0,0.70) 24%, rgba(0,0,0,0.32) 52%, rgba(0,0,0,0.04) 100%)',
        },
      });
    }

    return (
      <View pointerEvents="none" style={styles.nativeFadeWrapper}>
        <View style={styles.nativeFadeBottom} />
        <View style={styles.nativeFadeMiddle} />
        <View style={styles.nativeFadeTop} />
      </View>
    );
  };

  const renderOfferBackground = (offer) => {
    const offerMedia = getOfferMedia(offer);

    if (!offerMedia?.url) {
      return (
        <View style={styles.defaultCardBackground}>
          <View style={styles.defaultBackgroundGlowOne} />
          <View style={styles.defaultBackgroundGlowTwo} />
        </View>
      );
    }

    if (offerMedia.type === 'video' && Platform.OS === 'web') {
      return (
        <View style={styles.offerVideoLayer}>
          {React.createElement('video', {
            src: offerMedia.url,
            autoPlay: true,
            muted: true,
            loop: true,
            playsInline: true,
            controls: false,
            style: {
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '100%',
              objectFit: 'cover',
              pointerEvents: 'none',
              border: 'none',
              outline: 'none',
            },
          })}
        </View>
      );
    }

    if (offerMedia.type === 'image') {
      return (
        <Image
          source={{ uri: offerMedia.url }}
          resizeMode="cover"
          style={styles.offerBackgroundImage}
        />
      );
    }

    return (
      <View style={styles.defaultCardBackground}>
        <View style={styles.defaultBackgroundGlowOne} />
        <View style={styles.defaultBackgroundGlowTwo} />
      </View>
    );
  };

  const renderOfferCard = (offer) => {
    const stock = Number(offer.Stock || 0);
    const soldOut = stock <= 0;

    return (
      <TouchableOpacity
        key={String(offer.CollabID)}
        style={[
          styles.offerCard,
          Platform.OS === 'web' && styles.webClickable,
        ]}
        activeOpacity={0.94}
        accessibilityRole="link"
        accessibilityLabel={`View voucher from ${offer.BrandName}`}
        onPress={() => openVoucherLink(offer.CollabID)}
      >
        <View style={styles.offerImageSection}>
          {renderOfferBackground(offer)}

          <View pointerEvents="none" style={styles.offerImageSoftOverlay} />

          {renderGradientFade()}

          <View style={styles.offerImageContent}>
            <View style={styles.offerTopLine}>
              <View
                style={[
                  styles.stockBadge,
                  soldOut && styles.stockBadgeSoldOut,
                ]}
              >
                <View
                  style={[
                    styles.stockBadgeDot,
                    soldOut && styles.stockBadgeDotSoldOut,
                  ]}
                />

                <Text
                  style={[
                    styles.stockBadgeText,
                    soldOut && styles.stockBadgeTextSoldOut,
                  ]}
                >
                  {soldOut ? 'Sold out' : `${formatNumber(stock)} left`}
                </Text>
              </View>
            </View>

            <View style={styles.offerMainCopy}>
              <Text style={styles.offerBrandName} numberOfLines={1}>
                {offer.BrandName || 'Voucher offer'}
              </Text>

              <Text style={styles.offerDiscount}>
                {Number(offer.Discount || 0)}% off
              </Text>

              <Text style={styles.offerSpend}>
                when you spend up to £{Number(offer.OnSpend || 0)}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.offerActionSection}>
          <View style={styles.offerCreatorBlock}>
            <Text style={styles.sharedByLabel}>SHARED BY</Text>

            <View style={styles.offerCreatorNameRow}>
              <Text style={styles.offerCreatorName} numberOfLines={1}>
                {creator?.Promoter || Promoter}
              </Text>

              <View style={styles.smallVerificationBadge}>
                <Ionicons name="checkmark" size={10} color="#ffffff" />
              </View>
            </View>
          </View>

          <View style={styles.openVoucherButton}>
            <Text style={styles.openVoucherButtonText}>
              {soldOut ? 'View offer' : 'View voucher'}
            </Text>

            <View style={styles.openVoucherArrow}>
              <Ionicons name="arrow-forward" size={16} color="#111111" />
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingPage}>
        <View style={styles.loadingLogoRow}>
          <Text style={styles.loadingLogoCollab}>collab</Text>
          <Text style={styles.loadingLogoBay}>bay</Text>
        </View>

        <ActivityIndicator size="large" color="#111111" />

        <Text style={styles.loadingText}>Loading vouchers...</Text>
      </View>
    );
  }

  if (loadError && offers.length === 0) {
    return (
      <ScrollView
        style={styles.page}
        contentContainerStyle={styles.errorPageContent}
      >
        <View style={styles.errorCard}>
          <View style={styles.errorIconCircle}>
            <Ionicons
              name="alert-circle-outline"
              size={27}
              color="#111111"
            />
          </View>

          <Text style={styles.errorTitle}>Profile unavailable</Text>

          <Text style={styles.errorText}>{loadError}</Text>

          <TouchableOpacity
            style={styles.retryButton}
            activeOpacity={0.9}
            onPress={() => fetchCreatorProfile(false)}
          >
            <Text style={styles.retryButtonText}>Try again</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView
      style={styles.page}
      contentContainerStyle={styles.pageContent}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={() => fetchCreatorProfile(true)}
          tintColor="#111111"
        />
      }
    >
      <View style={styles.topSupportWrapper}>
        <Text style={styles.topSupportText}>
          Support your favourite creators with{' '}
          <Text style={styles.logoCollab}>collab</Text>
          <Text style={styles.logoBay}>bay</Text> ©
        </Text>
      </View>

      <View style={styles.profileCard}>
        <View style={styles.profileBackgroundGlowOne} />
        <View style={styles.profileBackgroundGlowTwo} />

        <View style={styles.profileCardContent}>
          {renderCreatorAvatar()}

          <View style={styles.creatorNameRow}>
            <Text style={styles.creatorName} numberOfLines={1}>
              {creator?.Promoter || Promoter}
            </Text>

            <View style={styles.creatorVerificationBadge}>
              <Ionicons name="checkmark" size={12} color="#ffffff" />
            </View>
          </View>

          <Text style={styles.creatorSubtitle}>
            Hand-picked vouchers shared with their audience
          </Text>

          <View style={styles.statsPanel}>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>
                {formatNumber(summary.LiveOffers)}
              </Text>
              <Text style={styles.statLabel}>
                {Number(summary.LiveOffers) === 1 ? 'live offer' : 'live offers'}
              </Text>
            </View>

            <View style={styles.statDivider} />

            <View style={styles.statItem}>
              <Text style={styles.statNumber}>
                {formatNumber(summary.AvailableStock)}
              </Text>
              <Text style={styles.statLabel}>vouchers left</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.listHeader}>
        <View style={styles.listHeaderCopy}>
          <Text style={styles.listHeaderTitle}>Available vouchers</Text>
          <Text style={styles.listHeaderSubtitle}>
            Tap any offer to view and purchase.
          </Text>
        </View>

        <View style={styles.offerCountCircle}>
          <Text style={styles.offerCountValue}>
            {formatNumber(summary.TotalOffers)}
          </Text>
        </View>
      </View>

      {offers.length > 0 ? (
        offers.map((offer) => renderOfferCard(offer))
      ) : (
        <View style={styles.emptyCard}>
          <View style={styles.emptyIconCircle}>
            <Ionicons name="ticket-outline" size={25} color="#111111" />
          </View>

          <Text style={styles.emptyTitle}>No vouchers live right now</Text>

          <Text style={styles.emptyText}>
            This creator does not currently have any available voucher offers.
          </Text>
        </View>
      )}

      <View style={styles.footer}>
        <View style={styles.footerLine} />

        <Text style={styles.footerMessage}>
          Discover local offers with{' '}
          <Text style={styles.footerLogoCollab}>collab</Text>
          <Text style={styles.footerLogoBay}>bay</Text> ©
        </Text>

        <Text style={styles.footerWebsite}>collabbay.com</Text>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: '#f5f4f1',
  },

  pageContent: {
    width: '100%',
    maxWidth: 560,
    alignSelf: 'center',
    paddingHorizontal: 16,
    paddingTop: 17,
    paddingBottom: 54,
  },

  loadingPage: {
    flex: 1,
    backgroundColor: '#f5f4f1',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },

  loadingLogoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 25,
  },

  loadingLogoCollab: {
    color: '#111111',
    fontSize: 27,
    fontWeight: '600',
    letterSpacing: -0.8,
  },

  loadingLogoBay: {
    color: '#111111',
    fontSize: 27,
    fontWeight: '900',
    letterSpacing: -0.8,
  },

  loadingText: {
    marginTop: 16,
    color: '#6f6c66',
    fontSize: 14,
    fontWeight: '600',
  },

  errorPageContent: {
    flexGrow: 1,
    width: '100%',
    maxWidth: 560,
    alignSelf: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
    paddingVertical: 32,
  },

  errorCard: {
    backgroundColor: '#ffffff',
    borderRadius: 30,
    borderWidth: 1,
    borderColor: '#ebe8e1',
    paddingHorizontal: 25,
    paddingVertical: 34,
    alignItems: 'center',
  },

  errorIconCircle: {
    width: 58,
    height: 58,
    borderRadius: 29,
    backgroundColor: '#FDF05D',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 17,
  },

  errorTitle: {
    color: '#111111',
    fontSize: 24,
    fontWeight: '800',
    letterSpacing: -0.6,
  },

  errorText: {
    marginTop: 8,
    color: '#6f6c66',
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
  },

  retryButton: {
    backgroundColor: '#111111',
    minHeight: 52,
    borderRadius: 999,
    paddingHorizontal: 29,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 23,
  },

  retryButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
  },

  topSupportWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 15,
    paddingHorizontal: 10,
  },

  topSupportText: {
    color: '#6d6962',
    fontSize: 12,
    lineHeight: 18,
    fontWeight: '600',
    textAlign: 'center',
  },

  logoCollab: {
    color: '#111111',
    fontWeight: '600',
  },

  logoBay: {
    color: '#111111',
    fontWeight: '900',
  },

  profileCard: {
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#111111',
    borderRadius: 34,
    marginBottom: 27,
  },

  profileBackgroundGlowOne: {
    position: 'absolute',
    top: -88,
    right: -78,
    width: 245,
    height: 245,
    borderRadius: 123,
    backgroundColor: 'rgba(253,240,93,0.17)',
  },

  profileBackgroundGlowTwo: {
    position: 'absolute',
    bottom: -95,
    left: -76,
    width: 215,
    height: 215,
    borderRadius: 108,
    backgroundColor: 'rgba(255,255,255,0.06)',
  },

  profileCardContent: {
    alignItems: 'center',
    paddingTop: 27,
    paddingHorizontal: 22,
    paddingBottom: 22,
  },

  creatorAvatarOuter: {
    width: 94,
    height: 94,
    borderRadius: 47,
    padding: 4,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.52)',
    marginBottom: 16,
  },

  creatorAvatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 43,
    backgroundColor: '#efefef',
  },

  creatorAvatarFallback: {
    width: '100%',
    height: '100%',
    borderRadius: 43,
    backgroundColor: '#FDF05D',
    justifyContent: 'center',
    alignItems: 'center',
  },

  creatorAvatarFallbackText: {
    color: '#111111',
    fontSize: 25,
    fontWeight: '800',
  },

  creatorNameRow: {
    maxWidth: '100%',
    flexDirection: 'row',
    alignItems: 'center',
  },

  creatorName: {
    maxWidth: '82%',
    color: '#ffffff',
    fontSize: 27,
    lineHeight: 33,
    fontWeight: '800',
    letterSpacing: -0.7,
  },

  creatorVerificationBadge: {
    width: 21,
    height: 21,
    borderRadius: 11,
    backgroundColor: '#1D9BF0',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },

  creatorSubtitle: {
    marginTop: 7,
    color: '#c5c5c5',
    fontSize: 13,
    lineHeight: 19,
    fontWeight: '500',
    textAlign: 'center',
  },

  statsPanel: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    minHeight: 70,
    borderRadius: 23,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
    marginTop: 25,
  },

  statItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },

  statDivider: {
    width: 1,
    height: 34,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },

  statNumber: {
    color: '#ffffff',
    fontSize: 21,
    fontWeight: '800',
    letterSpacing: -0.4,
  },

  statLabel: {
    marginTop: 3,
    color: '#a9a9a9',
    fontSize: 11,
    fontWeight: '600',
  },

  listHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    paddingHorizontal: 2,
  },

  listHeaderCopy: {
    flex: 1,
    paddingRight: 14,
  },

  listHeaderTitle: {
    color: '#111111',
    fontSize: 22,
    lineHeight: 28,
    fontWeight: '800',
    letterSpacing: -0.45,
  },

  listHeaderSubtitle: {
    marginTop: 4,
    color: '#767168',
    fontSize: 13,
    fontWeight: '500',
  },

  offerCountCircle: {
    minWidth: 40,
    height: 40,
    paddingHorizontal: 11,
    borderRadius: 20,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e7e3db',
    justifyContent: 'center',
    alignItems: 'center',
  },

  offerCountValue: {
    color: '#111111',
    fontSize: 14,
    fontWeight: '700',
  },

  offerCard: {
    backgroundColor: '#ffffff',
    borderRadius: 29,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#eae7df',
    marginBottom: 17,
  },

  webClickable: {
    cursor: 'pointer',
  },

  offerImageSection: {
    height: 350,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#151515',
  },

  offerVideoLayer: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#151515',
  },

  offerBackgroundImage: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },

  defaultCardBackground: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#171717',
  },

  defaultBackgroundGlowOne: {
    position: 'absolute',
    top: -63,
    right: -70,
    width: 245,
    height: 245,
    borderRadius: 123,
    backgroundColor: 'rgba(253,240,93,0.19)',
  },

  defaultBackgroundGlowTwo: {
    position: 'absolute',
    bottom: -72,
    left: -60,
    width: 210,
    height: 210,
    borderRadius: 105,
    backgroundColor: 'rgba(255,255,255,0.07)',
  },

  offerImageSoftOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.10)',
  },

  nativeFadeWrapper: {
    ...StyleSheet.absoluteFillObject,
  },

  nativeFadeBottom: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '43%',
    backgroundColor: 'rgba(0,0,0,0.84)',
  },

  nativeFadeMiddle: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '39%',
    height: '26%',
    backgroundColor: 'rgba(0,0,0,0.34)',
  },

  nativeFadeTop: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '62%',
    height: '16%',
    backgroundColor: 'rgba(0,0,0,0.10)',
  },

  offerImageContent: {
    position: 'relative',
    zIndex: 2,
    flex: 1,
    justifyContent: 'space-between',
    padding: 18,
  },

  offerTopLine: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },

  stockBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 38,
    borderRadius: 999,
    paddingHorizontal: 13,
    backgroundColor: 'rgba(17,17,17,0.62)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },

  stockBadgeSoldOut: {
    backgroundColor: 'rgba(255,255,255,0.94)',
    borderColor: 'rgba(255,255,255,0.94)',
  },

  stockBadgeDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: '#FDF05D',
    marginRight: 7,
  },

  stockBadgeDotSoldOut: {
    backgroundColor: '#999999',
  },

  stockBadgeText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '700',
  },

  stockBadgeTextSoldOut: {
    color: '#111111',
  },

  offerMainCopy: {
    paddingBottom: 3,
  },

  offerBrandName: {
    color: '#FDF05D',
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 8,
  },

  offerDiscount: {
    color: '#ffffff',
    fontSize: 48,
    lineHeight: 52,
    fontWeight: '800',
    letterSpacing: -1.7,
  },

  offerSpend: {
    color: '#f0f0f0',
    fontSize: 19,
    fontWeight: '600',
    marginTop: 3,
  },

  offerActionSection: {
    minHeight: 89,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
  },

  offerCreatorBlock: {
    flex: 1,
    paddingRight: 10,
  },

  sharedByLabel: {
    color: '#918c84',
    fontSize: 10,
    lineHeight: 13,
    fontWeight: '700',
    letterSpacing: 0.85,
    marginBottom: 5,
  },

  offerCreatorNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  offerCreatorName: {
    maxWidth: '80%',
    color: '#111111',
    fontSize: 15,
    fontWeight: '700',
  },

  smallVerificationBadge: {
    width: 17,
    height: 17,
    borderRadius: 9,
    backgroundColor: '#1D9BF0',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 6,
  },

  openVoucherButton: {
    minHeight: 53,
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 999,
    backgroundColor: '#111111',
    paddingLeft: 17,
    paddingRight: 10,
  },

  openVoucherButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '700',
    marginRight: 10,
  },

  openVoucherArrow: {
    width: 31,
    height: 31,
    borderRadius: 16,
    backgroundColor: '#FDF05D',
    justifyContent: 'center',
    alignItems: 'center',
  },

  emptyCard: {
    backgroundColor: '#ffffff',
    borderRadius: 29,
    borderWidth: 1,
    borderColor: '#eae7df',
    paddingHorizontal: 24,
    paddingVertical: 42,
    alignItems: 'center',
  },

  emptyIconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#FDF05D',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 17,
  },

  emptyTitle: {
    color: '#111111',
    fontSize: 20,
    lineHeight: 26,
    fontWeight: '800',
    textAlign: 'center',
  },

  emptyText: {
    maxWidth: 320,
    marginTop: 8,
    color: '#767168',
    fontSize: 14,
    lineHeight: 21,
    fontWeight: '500',
    textAlign: 'center',
  },

  footer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 27,
    paddingBottom: 14,
  },

  footerLine: {
    width: '100%',
    height: 1,
    backgroundColor: '#e2ded5',
    marginBottom: 23,
  },

  footerMessage: {
    color: '#767168',
    fontSize: 12,
    lineHeight: 18,
    fontWeight: '500',
    textAlign: 'center',
  },

  footerLogoCollab: {
    color: '#111111',
    fontWeight: '600',
  },

  footerLogoBay: {
    color: '#111111',
    fontWeight: '900',
  },

  footerWebsite: {
    marginTop: 7,
    color: '#111111',
    fontSize: 12,
    fontWeight: '700',
  },
});

export default CreatorVouchers;